/**
 * Shared sell-listing validation contract (Phase 3).
 * Create UI, edit UI, POST /api/sell/listings, and PATCH /api/items/[id]
 * share absolute price bounds and free-text asserts from this module /
 * `sellListingValidation` — do not re-implement floors/ceilings in routes.
 *
 * Spec form checks: `listingSpecPlugin.ts` (Phase 4).
 */

import { assertListingFreeTextAllowed } from "@/lib/listing/listingFreeTextPolicy";

export const SELL_LISTING_MIN_PRICE = 20_000;
export const SELL_LISTING_MAX_PRICE = 3_000_000;

export type ListingFreeTextAssertResult = ReturnType<
  typeof assertListingFreeTextAllowed
>;

export type AbsolutePriceCheckResult =
  | { ok: true; numericPrice: number }
  | { ok: false; message: string };

/** Parse seller price string (allows thousands separators). */
export function parseSellListingNumericPrice(price: string | number): number {
  if (typeof price === "number") return price;
  return Number(String(price).replace(/,/g, ""));
}

/**
 * Absolute marketplace price floor/ceiling (not market band).
 * Messages match {@link validateSellListingField}("price", …).
 */
export function assertSellListingAbsolutePrice(
  price: string | number,
): AbsolutePriceCheckResult {
  if (typeof price === "string" && !price.trim()) {
    return { ok: false, message: "Price is required" };
  }
  const numericPrice = parseSellListingNumericPrice(price);
  if (!Number.isFinite(numericPrice) || numericPrice < SELL_LISTING_MIN_PRICE) {
    return { ok: false, message: "Price must be at least ₦20,000" };
  }
  if (numericPrice > SELL_LISTING_MAX_PRICE) {
    return { ok: false, message: "Price cannot exceed ₦3,000,000" };
  }
  return { ok: true, numericPrice };
}

/** Free-text policy for title / description / defects (create + edit + API). */
export function assertSellListingFreeText(
  title: string,
  description: string,
  defects: string,
): ListingFreeTextAssertResult {
  return assertListingFreeTextAllowed(title, description, defects);
}

/**
 * Intentional create vs edit differences (do not unify):
 * - **Create images:** exactly 3 files with one main (`validateSellListingField("images")` /
 *   `assertSellListingImagesReady`) — required before insert.
 * - **Edit images:** optional replace / slot update via `persistEditListingImages`;
 *   existing CDN URLs without `file` are allowed; do not use create image asserts.
 * - **Edit pickup:** edit may require ≥1 pickup day on full save; create allows empty pickup.
 * - **Edit mutability:** `canSellerEditListing` / review-round gates before PATCH.
 * - **Edit spec enforcement:** PATCH may skip seating re-validate when spec fields untouched
 *   (photo-only fixes); create always builds attributes and enforces when wave is on.
 */
export const SELL_LISTING_CREATE_EDIT_DIFFERENCES = {
  images: "create_requires_three_files_edit_allows_existing_urls",
  pickup: "create_optional_edit_may_require_days_on_full_save",
  mutability: "edit_gated_by_listingMutability_and_review_rounds",
  specOnPatch: "edit_may_skip_spec_when_fields_untouched",
} as const;
